import { ChangeEvent, useState } from 'react';
import { useAudioPlayerContext } from './context/AudioPlayerContext';
import VolumeOff from '../../assets/icons/audioplayer/volume_off.svg';
import VolumeDown from '../../assets/icons/audioplayer/volume_down.svg';
import VolumeUp from '../../assets/icons/audioplayer/volume_up.svg';

export const VolumeControl = () => {
  const { volume, setVolume, muteVolume, setMuteVolume } = useAudioPlayerContext();
  const [lastVolume, setLastVolume] = useState<number>(volume);

  // Handle volume slider changes
  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setLastVolume(newVolume); // Save the last non-zero volume
    }
    if (muteVolume && newVolume > 0) {
      setMuteVolume(false); // Automatically unmute when adjusting volume
    }
  };

  // Toggle mute/unmute
  const toggleMute = () => {
    if (muteVolume) {
      setVolume(lastVolume); // Restore last volume when unmuting
    } else {
      setLastVolume(volume > 0 ? volume : 60); // Save current or default volume
      setVolume(0); // Set volume to 0 when muting
    }
    setMuteVolume((prevMute) => !prevMute); // Toggle mute state
  };

  return (
    <div className="relative group">
      <div className="flex items-center">
        {/* Mute/Unmute Button */}
        <button
          title="Volume"
          className="peer cursor-pointer ml-1 relative"
          onClick={toggleMute}
        >
          {muteVolume || volume === 0 ? (
            <img src={VolumeOff} srcSet={`${VolumeOff} 1x, ${VolumeOff} 2x`} loading="lazy" alt="Volume Off" className="w-[48px] h-[48px]" />
          ) : volume < 40 ? (
            <img src={VolumeDown} srcSet={`${VolumeDown} 1x, ${VolumeDown} 2x`} loading="lazy" alt="Volume Down" className="w-[48px] h-[48px]" />
          ) : (
            <img src={VolumeUp} srcSet={`${VolumeUp} 1x, ${VolumeUp} 2x`} loading="lazy" alt="Volume Up" className="w-[48px] h-[48px]" />
          )}
        </button>

        {/* Volume Slider */}
        <div
          className="absolute top-[-1.8rem] left-[-2rem] lg:top-0.5 lg:left-[-4.5em] w-[100px] lg:w-[80px] opacity-0 group-hover:opacity-100 peer-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-500 pointer-events-none group-hover:pointer-events-auto"
        >
          <input
            type="range"
            min={0}
            max={100}
            value={muteVolume ? 0 : volume}
            className="w-full h-2 rounded appearance-none bg-gradient-to-r from-primary-600 to-gray-400 focus:outline-none cursor-pointer 
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 
                       [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:transform [&::-webkit-slider-thumb]:transition-transform 
                       [&::-webkit-slider-thumb]:duration-200 [&::-webkit-slider-thumb]:hover:scale-150 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 
                       [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white 
                       [&::-moz-range-thumb]:transform [&::-moz-range-thumb]:transition-transform [&::-moz-range-thumb]:duration-200 [&::-moz-range-thumb]:hover:scale-150"
            onChange={handleVolumeChange}
            style={{
              background: `linear-gradient(to right, #C81C22 ${muteVolume ? 0 : volume}%, #6C6C6C ${muteVolume ? 0 : volume}%)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};